import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import '../../components/Button.css';

function explainMarketingApiError(e) {
  const data = e.response?.data;
  const err = data?.error || data?.message;
  if (err === 'Not found') {
    const path = data?.path ? String(data.path) : '';
    const missingApiPrefix = path.startsWith('/marketing/') && !path.startsWith('/api/');
    const lines = [
      'De server geeft “Not found” (404) voor deze actie.',
      missingApiPrefix
        ? 'Het verzoek mist het /api-voorvoegsel — controleer REACT_APP_API_URL op Vercel (basis-URL van Railway, zonder extra pad).'
        : 'Railway heeft mogelijk nog niet de nieuwste deploy (commit met import dagdeal/dagknaller). Redeploy backend of wacht enige minuten.',
      'Log daarna opnieuw in en probeer opnieuw.',
    ];
    if (path) lines.push(`Technisch pad: ${path}`);
    return lines.join('\n');
  }
  if (typeof err === 'string' && err.includes('VBP REST-endpoint')) {
    return `${err}\n\nUpload Verzendbelofte plugin v7.5.10+ op WordPress (REST marketing-deals-today).`;
  }
  if (typeof err === 'string' && err.includes('voorraad_feed_api_key')) {
    return `${err}\n\nGebruik dezelfde API-sleutel als in Verzendbelofte → Dagdeal-tab → webhook.`;
  }
  return err || e.message || 'Onbekende fout';
}

export default function InstagramSettingsPanel() {
  const [igConfig, setIgConfig] = useState(null);
  const [igForm, setIgForm] = useState({
    label: 'Test Instagram',
    facebook_page_id: '',
    instagram_business_account_id: '',
    page_access_token: '',
  });
  const [igAutoForm, setIgAutoForm] = useState({
    enabled: false,
    default_publish_time: '10:00',
    max_posts_per_day: 2,
    timezone: 'Europe/Amsterdam',
    vbp_deals_enabled: true,
    vbp_dagknaller_time: '09:00',
    vbp_dagdeal_time: '10:30',
    deal_image_ai_enabled: false,
    vbp_story_enabled: true,
    vbp_dagdeal_publish_target: 'story',
    vbp_dagknaller_publish_target: 'both',
    instagram_story_manual_link: true,
  });

  const vbpTargetOptions = [
    ['story', 'Alleen Story (9:16)'],
    ['feed', 'Alleen feed-post'],
    ['both', 'Feed én Story'],
  ];
  const [igTestImage, setIgTestImage] = useState('');
  const [igTestCaption, setIgTestCaption] = useState('Testpost 4SHINE Marketingkalender');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [cfg, auto] = await Promise.all([
      api.get('/marketing/instagram/config'),
      api.get('/marketing/instagram/auto-config'),
    ]);
    setIgConfig(cfg.data);
    setIgForm((f) => ({
      ...f,
      label: cfg.data?.label || f.label,
      facebook_page_id: cfg.data?.facebook_page_id || '',
      instagram_business_account_id: cfg.data?.instagram_business_account_id || '',
    }));
    setIgAutoForm({
      enabled: Boolean(auto.data?.enabled),
      default_publish_time: auto.data?.default_publish_time || '10:00',
      max_posts_per_day: auto.data?.max_posts_per_day ?? 2,
      timezone: auto.data?.timezone || 'Europe/Amsterdam',
      vbp_deals_enabled: auto.data?.vbp_deals_enabled !== false,
      vbp_dagknaller_time: auto.data?.vbp_dagknaller_time || '09:00',
      vbp_dagdeal_time: auto.data?.vbp_dagdeal_time || '10:30',
      deal_image_ai_enabled: Boolean(auto.data?.deal_image_ai_enabled),
      vbp_story_enabled: auto.data?.vbp_story_enabled !== false,
      vbp_dagdeal_publish_target: auto.data?.vbp_dagdeal_publish_target || 'story',
      vbp_dagknaller_publish_target: auto.data?.vbp_dagknaller_publish_target || 'both',
      instagram_story_manual_link: auto.data?.instagram_story_manual_link !== false,
    });
  }, []);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  const saveIgConfig = async () => {
    setBusy(true);
    try {
      const res = await api.put('/marketing/instagram/config', igForm);
      setIgConfig(res.data);
      alert('Instagram-instellingen opgeslagen');
    } catch (e) {
      alert(explainMarketingApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const discoverIg = async () => {
    setBusy(true);
    try {
      const res = await api.post('/marketing/instagram/discover', igForm);
      setIgConfig(res.data);
      setIgForm((f) => ({
        ...f,
        instagram_business_account_id: res.data.instagram_business_account_id || '',
        facebook_page_id: res.data.facebook_page_id || f.facebook_page_id,
      }));
      alert(res.data.username ? `Gekoppeld aan @${res.data.username}` : 'IG Business ID opgeslagen');
    } catch (e) {
      const err = e.response?.data?.error;
      alert(typeof err === 'string' ? err : err?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  const testIgPost = async () => {
    setBusy(true);
    try {
      await api.post('/marketing/instagram/test-post', {
        image_url: igTestImage,
        caption: igTestCaption,
      });
      alert('Testpost verstuurd — check Instagram.');
    } catch (e) {
      const err = e.response?.data?.error;
      alert(typeof err === 'string' ? err : err?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  const saveIgAutoConfig = async () => {
    setBusy(true);
    try {
      const res = await api.put('/marketing/instagram/auto-config', igAutoForm);
      setIgAutoForm(res.data);
      alert('Automatisering opgeslagen');
    } catch (e) {
      alert(explainMarketingApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const runIgSchedulerNow = async () => {
    setBusy(true);
    try {
      const res = await api.post('/marketing/instagram/run-scheduled');
      const errTxt =
        res.data.errors?.length > 0
          ? `\nFouten: ${res.data.errors.map((x) => x.title).join(', ')}`
          : '';
      alert(
        `Instagram-posts verstuurd: ${res.data.posted || 0}${res.data.skipped ? ` (${res.data.reason})` : ''}${errTxt}\n\nDit maakt geen nieuwe kalenderitems — gebruik “Importeer dagdeal/dagknaller” om posts in het overzicht te zetten.`
      );
    } catch (e) {
      alert(explainMarketingApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const importVbpDeals = async () => {
    setBusy(true);
    try {
      const res = await api.post('/marketing/vbp/sync-today');
      const lines = (res.data?.results || []).map((r) => {
        const tile =
          r.image_rendered === true
            ? ' — tegel met opmaak OK'
            : r.render_error
              ? ` — geen opmaak: ${r.render_error}`
              : r.skipped
                ? ' — alleen sfeerfoto (klik Dagdeal-tegel in feed)'
                : '';
        if (r.created) {
          return `${r.kind}: aangemaakt (event #${r.event_id})${tile}`;
        }
        if (r.skipped) {
          return `${r.kind || '?'}: ${r.reason}${r.event_id ? ` (#${r.event_id})` : ''}${tile}`;
        }
        return JSON.stringify(r);
      });
      alert(`Import Woo (${res.data?.day || 'vandaag'}):\n${lines.join('\n') || 'Geen resultaat'}`);
      window.dispatchEvent(new Event('marketing-ig-feed-refresh'));
    } catch (e) {
      alert(explainMarketingApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="marketing-ig-settings">
      <section className="card marketing-ig-settings-section">
        <h2>Koppeling Meta / Instagram</h2>
        <p className="muted marketing-ig-help">
          Professioneel IG-account + Facebook-pagina + app <strong>auto-koppeling-inkoopsoftware</strong>. Page access token
          blijft op de server (niet zichtbaar na opslaan).
        </p>
        <p className="muted">
          Status:{' '}
          {igConfig?.configured ? (
            <strong className="ok">
              Gekoppeld — IG ID {igConfig.instagram_business_account_id}
              {igConfig.facebook_page_id ? ` (pagina ${igConfig.facebook_page_id})` : ''}
            </strong>
          ) : (
            <span>Nog niet compleet</span>
          )}
        </p>
        <div className="marketing-ig-form-grid">
          <label>
            Label / accountnaam
            <input
              className="form-input"
              value={igForm.label}
              onChange={(e) => setIgForm((f) => ({ ...f, label: e.target.value }))}
            />
          </label>
          <label>
            Facebook-pagina-ID
            <input
              className="form-input"
              value={igForm.facebook_page_id}
              onChange={(e) => setIgForm((f) => ({ ...f, facebook_page_id: e.target.value }))}
            />
          </label>
          <label>
            Page access token
            <input
              className="form-input"
              type="password"
              value={igForm.page_access_token}
              onChange={(e) => setIgForm((f) => ({ ...f, page_access_token: e.target.value }))}
              placeholder={igConfig?.has_token ? '(opgeslagen — leeg laten)' : 'EAA…'}
            />
          </label>
          <label>
            IG Business account ID
            <input
              className="form-input"
              value={igForm.instagram_business_account_id}
              onChange={(e) => setIgForm((f) => ({ ...f, instagram_business_account_id: e.target.value }))}
            />
          </label>
        </div>
        <div className="marketing-detail-actions">
          <button type="button" className="btn btn-secondary" onClick={saveIgConfig} disabled={busy}>
            Opslaan
          </button>
          <button type="button" className="btn btn-secondary" onClick={discoverIg} disabled={busy}>
            IG-account ophalen
          </button>
        </div>
      </section>

      <section className="card marketing-ig-settings-section">
        <h2>Testpost</h2>
        <label className="marketing-plan-label">
          Publieke afbeelding (https)
          <input
            className="form-input"
            value={igTestImage}
            onChange={(e) => setIgTestImage(e.target.value)}
            placeholder="https://www.4shine.nl/wp-content/uploads/…"
          />
        </label>
        <label className="marketing-plan-label">
          Caption
          <input className="form-input" value={igTestCaption} onChange={(e) => setIgTestCaption(e.target.value)} />
        </label>
        <button type="button" className="btn btn-primary" onClick={testIgPost} disabled={busy || !igConfig?.configured}>
          Testpost naar Instagram
        </button>
      </section>

      <section className="card marketing-ig-settings-section">
        <h2>Automatisch posten</h2>
        <p className="muted">
          Goedgekeurde campagnes worden op datum + tijd gepost (sfeerfoto + caption + shopping-tag indien catalogus
          gekoppeld). Controle elke 5 minuten ({igAutoForm.timezone}).
        </p>
        <label className="marketing-ig-checkbox">
          <input
            type="checkbox"
            checked={igAutoForm.enabled}
            onChange={(e) => setIgAutoForm((f) => ({ ...f, enabled: e.target.checked }))}
          />
          Automatisering aan
        </label>
        <div className="marketing-ig-form-grid">
          <label>
            Standaard tijd
            <input
              className="form-input"
              value={igAutoForm.default_publish_time}
              onChange={(e) => setIgAutoForm((f) => ({ ...f, default_publish_time: e.target.value }))}
            />
          </label>
          <label>
            Max posts / dag
            <input
              className="form-input"
              type="number"
              min={1}
              max={10}
              value={igAutoForm.max_posts_per_day}
              onChange={(e) =>
                setIgAutoForm((f) => ({ ...f, max_posts_per_day: parseInt(e.target.value, 10) || 2 }))
              }
            />
          </label>
        </div>
        <label className="marketing-ig-checkbox">
          <input
            type="checkbox"
            checked={igAutoForm.vbp_deals_enabled}
            onChange={(e) => setIgAutoForm((f) => ({ ...f, vbp_deals_enabled: e.target.checked }))}
          />
          Woo dagdeal / dagknaller → automatisch Instagram (webhook Verzendbelofte)
        </label>
        <label className="marketing-ig-checkbox">
          <input
            type="checkbox"
            checked={igAutoForm.vbp_story_enabled}
            onChange={(e) => setIgAutoForm((f) => ({ ...f, vbp_story_enabled: e.target.checked }))}
          />
          Stories toestaan voor dagdeal/dagknaller (9:16-tegel)
        </label>
        <label className="marketing-ig-checkbox">
          <input
            type="checkbox"
            checked={igAutoForm.instagram_story_manual_link}
            onChange={(e) => setIgAutoForm((f) => ({ ...f, instagram_story_manual_link: e.target.checked }))}
          />
          Stories handmatig in Instagram-app (klikbare link-sticker) — aanbevolen
        </label>
        <p className="muted">
          Meta’s API kan geen klikbare “Link toevoegen”-sticker op stories. Met deze optie maakt Inkoop alleen de tegel +
          bewaart de URL; jij deelt de story in Instagram en plakt de link (zoals op je telefoon). Uitzetten = story
          automatisch online, maar zonder klikbare link.
        </p>
        <div className="marketing-ig-form-grid">
          <label>
            Dagdeal plaatsen als
            <select
              className="form-input"
              value={igAutoForm.vbp_dagdeal_publish_target}
              onChange={(e) =>
                setIgAutoForm((f) => ({ ...f, vbp_dagdeal_publish_target: e.target.value }))
              }
            >
              {vbpTargetOptions.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Dagknaller plaatsen als
            <select
              className="form-input"
              value={igAutoForm.vbp_dagknaller_publish_target}
              onChange={(e) =>
                setIgAutoForm((f) => ({ ...f, vbp_dagknaller_publish_target: e.target.value }))
              }
            >
              {vbpTargetOptions.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="marketing-ig-checkbox">
          <input
            type="checkbox"
            checked={igAutoForm.deal_image_ai_enabled}
            onChange={(e) => setIgAutoForm((f) => ({ ...f, deal_image_ai_enabled: e.target.checked }))}
          />
          Dagdeal-tegel standaard via OpenAI (experimenteel; vaste layout is standaard)
        </label>
        <div className="marketing-ig-form-grid">
          <label>
            Tijd dagknaller-post
            <input
              className="form-input"
              value={igAutoForm.vbp_dagknaller_time}
              onChange={(e) => setIgAutoForm((f) => ({ ...f, vbp_dagknaller_time: e.target.value }))}
            />
          </label>
          <label>
            Tijd dagdeal-post
            <input
              className="form-input"
              value={igAutoForm.vbp_dagdeal_time}
              onChange={(e) => setIgAutoForm((f) => ({ ...f, vbp_dagdeal_time: e.target.value }))}
            />
          </label>
        </div>
        <p className="muted">
          In WordPress: Verzendbelofte → Dagdeal-tab → webhook-URL{' '}
          <code>/api/webhooks/vbp-dagdeal</code> + dezelfde API-sleutel als de voorraad-feed. Zet max posts/dag op
          minstens 2 als je dagdeal én dagknaller op dezelfde dag live zet. Standaard: dagdeal alleen Story, dagknaller feed + story.
        </p>
        <div className="marketing-detail-actions">
          <button type="button" className="btn btn-secondary" onClick={saveIgAutoConfig} disabled={busy}>
            Automatisering opslaan
          </button>
          <button type="button" className="btn btn-primary" onClick={importVbpDeals} disabled={busy}>
            Importeer dagdeal/dagknaller
          </button>
          <button type="button" className="btn btn-secondary" onClick={runIgSchedulerNow} disabled={busy}>
            Post ingeplande items nu
          </button>
        </div>
      </section>
    </div>
  );
}
